import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Briefcase, Eye, FlaskConical, Loader2, LogIn, PlayCircle, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { Pagination } from "@/components/Pagination";
import { PageHeader } from "@/components/PageHeader";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { FilterPills } from "@/components/FilterPills";
import { StatusChip } from "@/components/StatusChip";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/datetime";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "pending", label: "Pending" },
  { key: "rejected", label: "Rejected" },
  { key: "suspended", label: "Suspended" },
  { key: "archived", label: "Archived" },
  { key: "total", label: "Total" },
];

const PER_PAGE = 50;

export default function AdminWorkspacesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const [aggregates, setAggregates] = useState({});
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [emulatingId, setEmulatingId] = useState(null);
  const [emulateConfirm, setEmulateConfirm] = useState(null);
  const { user, emulate } = useAuth();
  const navigate = useNavigate();

  const load = async (opts = {}) => {
    const wantPage = opts.page ?? page;
    const wantTab = opts.tab ?? activeTab;
    try {
      const params = new URLSearchParams({
        page: String(wantPage),
        per_page: String(PER_PAGE),
      });
      if (wantTab && wantTab !== "total") params.set("status", wantTab);
      const { data } = await api.get(`/admin/workspaces?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setStatusCounts(data.status_counts || {});
      setAggregates(data.aggregates || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setLoading(true);
    load({ tab: key, page: 1 });
  };
  const handlePageChange = (p) => {
    setLoading(true);
    load({ page: p });
  };

  const handleConfirmEmulate = async () => {
    const target = emulateConfirm;
    if (!target) return;
    setEmulateConfirm(null);
    setEmulatingId(target.id);
    try {
      localStorage.setItem("probe_workspace_id", target.id);
      await emulate(target.owner_user_id);
      toast.success(`Now viewing as ${target.owner_email} in ${target.name}`);
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
      setEmulatingId(null);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Workspace",
      mobileVariant: "title",
      render: (w) => (
        <div className="flex flex-col">
          <span className="rt-primary">{w.name}</span>
          <span className="text-xs text-[#64748B] break-all" data-testid={`workspace-owner-${w.id}`}>
            {w.owner_email || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Owner Status",
      render: (w) => <StatusChip testId={`workspace-status-chip-${w.owner_status || "active"}`} status={w.owner_status || "active"} />,
    },
    {
      key: "type",
      label: "Type",
      render: (w) => {
        const isProbeInternal = w.name === "Probe Internal";
        const toggle = async () => {
          if (isProbeInternal) {
            toast.error("Probe Internal is always a team workspace.");
            return;
          }
          try {
            await api.patch(`/admin/workspaces/${w.id}/personal-flag`, { is_personal: !w.is_personal });
            toast.success(w.is_personal ? `Marked ${w.name} as team workspace` : `Marked ${w.name} as personal`);
            load();
          } catch (e) {
            toast.error(formatApiError(e));
          }
        };
        return (
          <button
            type="button"
            onClick={toggle}
            disabled={isProbeInternal}
            data-testid={`workspace-type-toggle-${w.id}`}
            title={isProbeInternal ? "Locked to team" : `Click to switch to ${w.is_personal ? "Team" : "Personal"}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide transition-colors ${
              w.is_personal
                ? "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"
                : "bg-[#3B82F6]/10 text-[#1D4ED8] hover:bg-[#3B82F6]/20"
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {w.is_personal ? <User size={10} /> : <Building2 size={10} />}
            {w.is_personal ? "Personal" : "Team"}
          </button>
        );
      },
    },
    {
      key: "members",
      label: "Members",
      render: (w) => <span className="text-[#334155]">{w.member_count}</span>,
    },
    {
      key: "tests",
      label: "Tests",
      render: (w) => <span className="text-[#334155]">{w.test_count}</span>,
    },
    {
      key: "runs",
      label: "Runs",
      render: (w) => <span className="text-[#334155]">{w.run_count}</span>,
    },
    {
      key: "created",
      label: "Created",
      render: (w) => (
        <span className="text-[#64748B] text-xs">
          {formatDate(w.created_at)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (w) => {
        const disabled =
          w.owner_is_super_admin || w.owner_user_id === user?.id || !w.owner_user_id;
        const title = w.owner_is_super_admin
          ? "Cannot emulate a super admin"
          : w.owner_user_id === user?.id
            ? "This is your account"
            : !w.owner_user_id
              ? "Workspace has no owner"
              : `Sign in as ${w.owner_email} in ${w.name}`;
        return (
          <button
            onClick={() => setEmulateConfirm(w)}
            disabled={disabled || emulatingId === w.id}
            data-testid={`workspace-emulate-button-${w.id}`}
            title={title}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#0F172A] hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {emulatingId === w.id ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />}
            Sign in as user
          </button>
        );
      },
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="admin-workspaces-page">
      <PageHeader
        title="Workspaces"
        description="Manage customer workspaces and activity."
      />

      <MetricGrid columns={4}>
        <MetricCard testId="ws-stat-total" label="Total Workspaces" icon={Briefcase} value={aggregates.total_workspaces ?? "—"} />
        <MetricCard testId="ws-stat-active" label="Active" icon={Activity} value={aggregates.active_workspaces ?? "—"} />
        <MetricCard testId="ws-stat-with-tests" label="With Tests" icon={FlaskConical} value={aggregates.workspaces_with_tests ?? "—"} />
        <MetricCard testId="ws-stat-runs" label="Total Runs" icon={PlayCircle} value={aggregates.total_runs ?? "—"} />
      </MetricGrid>

      <FilterPills
        testId="workspace-status-tab"
        tabs={STATUS_TABS}
        value={activeTab}
        onChange={handleTabChange}
        counts={statusCounts}
      />

      {loading ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="surface-card py-12 text-center text-[#64748B] text-sm">
          <Briefcase size={20} className="mx-auto mb-2 text-[#94A3B8]" />
          No workspaces in <span className="font-semibold capitalize">{activeTab}</span> yet.
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="workspaces-table"
            columns={columns}
            rows={items}
            getRowTestId={(w) => `workspace-row-${w.id}`}
          />
          <Pagination
            testId="workspaces-pagination"
            page={page}
            perPage={PER_PAGE}
            total={total}
            onChange={handlePageChange}
          />
        </>
      )}

      <AlertDialog open={!!emulateConfirm} onOpenChange={(open) => !open && setEmulateConfirm(null)}>
        <AlertDialogContent
          data-testid="workspace-emulate-confirm-dialog"
          className="rounded-xl border border-[#E5E7EB] bg-white p-0 max-w-md gap-0"
        >
          <AlertDialogHeader className="px-6 pt-6 pb-3 space-y-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6]">
              <Eye size={18} />
            </div>
            <p className="text-xs font-medium text-[#3B82F6]">Super Admin · Sign in as user</p>
            <AlertDialogTitle className="font-['Geist'] font-semibold tracking-tight text-2xl leading-tight text-[#0F172A]">
              Open this workspace as its owner?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#475569] leading-relaxed pt-1">
              You&apos;ll sign in as the workspace owner and the session will pre-select{" "}
              <span className="font-mono text-[12px] text-[#0F172A] bg-[#F1F5F9] px-1.5 py-0.5 rounded break-all">
                {emulateConfirm?.name}
              </span>
              . Every action is tagged in the emulation audit log under your admin account.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mx-6 my-4 border border-[#E5E7EB] bg-[#F8FAFC] rounded-lg p-3 space-y-1.5">
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">You</span>
              <span className="text-sm font-medium text-[#0F172A] break-all">{user?.email}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">Owner</span>
              <span className="text-sm font-medium text-[#0F172A] break-all" data-testid="workspace-emulate-target-email">
                {emulateConfirm?.owner_email}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">Workspace</span>
              <span className="text-sm font-medium text-[#0F172A] break-all">{emulateConfirm?.name}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#64748B] mt-0.5 shrink-0 w-20">Status</span>
              <span className="text-sm font-medium text-[#0F172A] capitalize">
                {emulateConfirm?.owner_status}
              </span>
            </div>
          </div>

          <AlertDialogFooter className="px-6 pb-6 pt-2 flex-row justify-end gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="workspace-emulate-confirm-cancel"
              className="rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] text-sm font-medium h-9 px-4 mt-0 hover:bg-[#F8FAFC]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEmulate}
              data-testid="workspace-emulate-confirm-accept"
              className="rounded-md bg-[#3B82F6] text-white text-sm font-medium h-9 px-4 hover:bg-[#2563EB] inline-flex items-center gap-1.5"
            >
              <LogIn size={13} />
              Sign in as user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
